import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";

import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  error: {
    color: "#c62828",
    marginTop: theme.spacing(1),
  },
  success: {
    color: "#1b7f3a",
    marginTop: theme.spacing(1),
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: theme.spacing(2),
  },
  preview: {
    width: "100%",
    minHeight: 640,
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    background: "#fff",
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    lineHeight: 1.5,
  },
});

async function readJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (err) {
    throw new Error("השרת לא החזיר את הדוח. נסו שוב בעוד רגע.");
  }
}

const CADENCE_LABEL = {
  daily: "יומי",
  weekly: "שבועי",
  monthly: "חודשי",
  journey: "מסע לקוח",
};

class PerformanceReportsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      busyCadence: null,
      sendingId: null,
      error: null,
      success: null,
      schedule: null,
      reports: [],
      previewHtml: "",
      previewId: null,
    };
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true, error: null });
    try {
      const headers = utils.getAdminAuthHeaders();
      const [scheduleRes, listRes] = await Promise.all([
        fetch(`${basePath}/api/v1/admin/performance-reports/schedule`, { headers }),
        fetch(`${basePath}/api/v1/admin/performance-reports`, { headers }),
      ]);
      const scheduleBody = await scheduleRes.json();
      const listBody = await listRes.json();
      if (!scheduleBody.ok) throw new Error(scheduleBody.error || "טעינת התזמון נכשלה");
      if (!listBody.ok) throw new Error(listBody.error || "טעינת הדוחות נכשלה");
      this.setState({
        loading: false,
        schedule: scheduleBody.schedule,
        reports: listBody.reports || [],
      });
    } catch (err) {
      this.setState({ loading: false, error: err.message });
    }
  };

  saveSchedule = async (patch) => {
    const schedule = { ...this.state.schedule, ...patch };
    this.setState({ schedule, error: null, success: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/performance-reports/schedule`, {
        method: "PUT",
        headers: {
          ...utils.getAdminAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(schedule),
      });
      const body = await res.json();
      if (!body.ok) throw new Error(body.error || "שמירת התזמון נכשלה");
      this.setState({ schedule: body.schedule, success: "התזמון נשמר" });
    } catch (err) {
      this.setState({ error: err.message });
    }
  };

  generate = async (cadence) => {
    this.setState({ busyCadence: cadence, error: null, success: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/performance-reports/generate`, {
        method: "POST",
        headers: {
          ...utils.getAdminAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ cadence }),
      });
      const body = await readJson(res);
      if (!body.ok) throw new Error(body.error || "יצירת הדוח נכשלה");
      this.setState({
        busyCadence: null,
        success: cadence === "journey" ? "דוח מסע הלקוח נוצר, ולא נשלח" : `הדוח ה${CADENCE_LABEL[cadence]} נוצר`,
      });
      await this.load();
      if (body.report?.id) this.preview(body.report.id);
    } catch (err) {
      this.setState({ busyCadence: null, error: err.message });
    }
  };

  preview = async (id) => {
    this.setState({ previewId: id, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/performance-reports/${id}`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const body = await readJson(res);
      if (!body.ok) throw new Error(body.error || "טעינת הדוח נכשלה");
      this.setState({ previewHtml: body.report?.html || "", previewId: id });
    } catch (err) {
      this.setState({ error: err.message, previewHtml: "" });
    }
  };

  send = async (id, audience) => {
    this.setState({ sendingId: `${id}:${audience}`, error: null, success: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/performance-reports/${id}/send`, {
        method: "POST",
        headers: {
          ...utils.getAdminAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ audience }),
      });
      const body = await res.json();
      if (!body.ok) throw new Error(body.error || "השליחה נכשלה");
      this.setState({
        sendingId: null,
        success: "הדוח נשלח אל itamar.arjuan@gmail.com",
        reports: (this.state.reports || []).map((report) =>
          report.id === id ? { ...report, sends: body.report?.sends || report.sends } : report
        ),
      });
    } catch (err) {
      this.setState({ sendingId: null, error: err.message });
      this.load();
    }
  };

  render() {
    const { classes } = this.props;
    const { loading, schedule, reports, busyCadence, sendingId, error, success, previewHtml } = this.state;
    if (loading) {
      return (
        <div className={classes.root}>
          <CircularProgress size={28} />
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Paper className={classes.section}>
          <Typography variant="h6">דוחות ביצועים</Typography>
          <Typography className={classes.meta}>
            דוח יומי ב-07:30, שבועי ביום שני ב-08:00, וחודשי בראשון לחודש ב-08:30 (שעון ישראל).
            השליחה האוטומטית הולכת רק אל itamar.arjuan@gmail.com.
          </Typography>
          {schedule && (
            <div>
              <FormControlLabel
                control={
                  <Switch
                    checked={schedule.dailySend !== false}
                    onChange={(event) => this.saveSchedule({ dailySend: event.target.checked })}
                    color="primary"
                  />
                }
                label="שליחה אוטומטית של הדוח היומי"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={schedule.weeklySend !== false}
                    onChange={(event) => this.saveSchedule({ weeklySend: event.target.checked })}
                    color="primary"
                  />
                }
                label="שליחה אוטומטית של הדוח השבועי"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={schedule.monthlySend !== false}
                    onChange={(event) => this.saveSchedule({ monthlySend: event.target.checked })}
                    color="primary"
                  />
                }
                label="שליחה אוטומטית של הדוח החודשי"
              />
            </div>
          )}
          <div className={classes.row}>
            {["daily", "weekly", "monthly"].map((cadence) => (
              <Button
                key={cadence}
                variant="contained"
                color="primary"
                disabled={Boolean(busyCadence)}
                onClick={() => this.generate(cadence)}
              >
                {busyCadence === cadence ? "מכין…" : `צור דוח ${CADENCE_LABEL[cadence]}`}
              </Button>
            ))}
          </div>
          <Typography className={classes.meta} style={{ marginTop: 16 }}>
            דוח מסע הלקוח נבנה רק בלחיצה. הוא לא נשלח לבד.
            הוא מרכז מוצרי גאורגיה שהגיעו למסך התשלום והלקוח עצר שם, מכל המסעות שנשמרו.
          </Typography>
          <div className={classes.row}>
            <Button
              variant="outlined"
              color="primary"
              disabled={Boolean(busyCadence)}
              onClick={() => this.generate("journey")}
            >
              {busyCadence === "journey" ? "מכין…" : "צור דוח מסע לקוח"}
            </Button>
          </div>
          {error && <Typography className={classes.error}>{error}</Typography>}
          {success && <Typography className={classes.success}>{success}</Typography>}
        </Paper>

        <Paper className={classes.section}>
          <Typography variant="h6">דוחות שמורים</Typography>
          <table className={classes.table}>
            <thead>
              <tr>
                <th align="right">סוג</th>
                <th align="right">תקופה</th>
                <th align="right">נשלח</th>
                <th align="right">פעולות</th>
              </tr>
            </thead>
            <tbody>
              {(reports || []).map((report) => {
                const generatedAt = new Date(report.updatedAt || report.createdAt || 0).getTime();
                const sent = (report.sends || []).filter(
                  (send) => !send.error && new Date(send.sentAt || 0).getTime() >= generatedAt
                ).length;
                return (
                  <tr key={report.id}>
                    <td>{CADENCE_LABEL[report.cadence] || report.cadence}</td>
                    <td>{report.period?.label || report.period?.from}</td>
                    <td>{sent ? `${sent} פעמים` : "עדיין לא"}</td>
                    <td>
                      <Button size="small" onClick={() => this.preview(report.id)}>
                        תצוגה
                      </Button>
                      <Button
                        size="small"
                        disabled={Boolean(sendingId)}
                        onClick={() => this.send(report.id, "me")}
                      >
                        {sendingId === `${report.id}:me` ? "שולח…" : "שלח לאיתמר"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {!reports.length && (
                <tr>
                  <td colSpan={4}>עדיין אין דוחות שמורים.</td>
                </tr>
              )}
            </tbody>
          </table>
        </Paper>

        {previewHtml ? (
          <Paper className={classes.section}>
            <Typography variant="h6">תצוגה</Typography>
            <iframe className={classes.preview} title="תצוגת דוח" srcDoc={previewHtml} />
          </Paper>
        ) : null}
      </div>
    );
  }
}

export default withStyles(styles)(PerformanceReportsPage);
